import type {
  NotificationPreferencePatch,
  NotificationPreferences,
} from '../notifications/preferences';
import { Platform } from 'react-native';
import Constants from 'expo-constants';
import { File, UploadType } from 'expo-file-system';
import { getDeviceTimeZone } from '../utils/locale';
import type { LinkPreview } from '../linkPreview/payload';
import { createReservationRequestId } from '../utils/requestId';
import type { GoogleAttempt, GoogleSession } from '../auth/googleAuthFlow';
export type ForwardMediaSource={sourceId:number;sourceRevision:number;kind:'image'|'voice'|'video'};
export type ForwardMediaAsset={operationId:string;destinationId:number;assetId:string;url:string;previewUrl?:string;
  kind:'image'|'voice'|'video';mimeType:string;sizeBytes:number;durationSeconds:number|null};
export type ForwardMediaProgress={operation:string;state:string;draft?:ForwardMediaAsset;messageId?:number};

export type ProfilePreferences = {
  show_read_receipts: boolean;
  /** 9.20.4 / D-060 — başkalarına "yazıyor" gösterilsin mi; eski sunucu döndürmez (açık sayılır). */
  show_typing?: boolean;
  who_can_message: 'everyone' | 'contacts' | 'nobody';
  who_can_call: 'everyone' | 'contacts' | 'nobody';
  keep_archived: boolean;
  /** 1.3.6 / D-657 — web profili arama motorlarında görünsün mü; eski sunucu döndürmez. */
  search_visible?: boolean;
  /** 1.3.7 / D-659 — web sayfası hiç açılsın mı; eski sunucu döndürmez (açık sayılır). */
  web_profile_visible?: boolean;
};

/**
 * D-302 — kişiye özel fiyatın isteğe bağlı türleri. Mesaj fiyatı (`price`)
 * zorunlu olduğu için burada yer almaz.
 *
 * `price_call` ANLIK aramanın, `price_call_scheduled` PLANLI aramanın dakika
 * ücretidir: D-299 genel tarafta ikisini ayırdı, D-302 aynı ayrımı kişiye özel
 * tarafta sürdürür. Aynı alana bağlamak satıcının planlı arama için ayrı fiyat
 * koyma imkânını sessizce kaldırırdı.
 */
export type ContactPriceKind =
  | 'price_voice'
  | 'price_call'
  | 'price_call_scheduled'
  | 'price_call_video'
  | 'price_time';

/** NULL = bu türde özel fiyat yok; satıcının genel fiyatı geçerli. */
export type ContactPrices = {
  price: number | null;
} & Record<ContactPriceKind, number | null>;

export type ContactPreferences = {
  translation_enabled: boolean;
  translation_language?: string | null;
  // Kişi bazındaki üç değerli istisnalar: null = genel politika, true = her
  // zaman izin ver, false = hiçbir zaman izin verme.
  call_allowed: boolean | null;
  message_allowed: boolean | null;
  configured: boolean;
};

export type ContactExceptionPage<T> = {
  items: T[];
  next_cursor: string | null;
};

export type InstagramConnectionStatus = {
  available: boolean;
  connected: boolean;
  connection: null | {
    username: string;
    accountType: 'BUSINESS' | 'CREATOR';
    profilePictureUrl: string | null;
    scopes: string[];
    expiresAt: string;
    status: 'active' | 'reauthorization_required';
    connectedAt: string;
    updatedAt: string;
  };
};

export type GoogleCalendarConnectionStatus = {
  available: boolean;
  connected: boolean;
  connection: null | {
    scopes: string[];
    status: 'active' | 'reauthorization_required';
    lastSyncedAt: string | null;
    connectedAt: string;
    updatedAt: string;
    calendars: GoogleCalendarOption[];
  };
};

/**
 * Microsoft (Outlook) calendar status — D-480.
 *
 * Not the Google type: two fields differ and the difference is real.
 *  - there is **no `calendars` list**, because per-calendar selection is not built for
 *    Microsoft yet;
 *  - there is a **`revocationUrl`**, because nosubject cannot revoke a Microsoft grant. The
 *    server has no disconnect route by design (D-479), so the surface must send the person to
 *    Microsoft's own permissions page rather than offer a button that cannot deliver.
 */
export type MicrosoftCalendarConnectionStatus = {
  available: boolean;
  connected: boolean;
  revocationUrl: string;
  connection: null | {
    scopes: string[];
    status: 'active' | 'reauthorization_required';
    lastSyncedAt: string | null;
    connectedAt: string;
    updatedAt: string;
  };
};

export type GoogleCalendarOption = {
  id: string;
  title: string;
  timeZone: string | null;
  color: string | null;
  primary: boolean;
  selected: boolean;
  lastSyncedAt: string | null;
};

export type GoogleCalendarEvent = {
  id: string;
  calendarId: string;
  calendarTitle: string;
  calendarColor: string | null;
  title: string;
  location: string | null;
  externalIdentity: string | null;
  originalStart: string | null;
  start: string;
  end: string;
  allDay: boolean;
  recurring: boolean;
  availability: 'free' | 'busy' | 'tentative' | 'unavailable';
  responseStatus: 'needsAction' | 'declined' | 'tentative' | 'accepted' | null;
  timeZone: string | null;
  busy: boolean;
};

// Deployed backend by default (so Expo Go on a phone just works).
// For local dev, set EXPO_PUBLIC_API_URL=http://localhost:3000
export const BASE_URL =
  process.env.EXPO_PUBLIC_API_URL ?? 'https://nosubject-api-534149427803.europe-west2.run.app';

// Foto/medya URL'i çöz: mutlak (GCS https) ise olduğu gibi kullan, değilse (eski
// /uploads/... yolu) BASE_URL ekle. GCS geçişinden (4.7.1) sonra ikisi de çalışır.
export function mediaUrl(u?: string | null): string {
  if (!u) return '';
  return /^https?:\/\//.test(u) ? u : `${BASE_URL}${u}`;
}

export class NetworkError extends Error {
  constructor() { super('Sunucuya ulaşılamıyor'); }
}

export class AuthError extends Error {
  constructor() { super('Oturum süresi doldu'); }
}

export type ProfilePhotoAttempt = {
  readonly token: string;
  readonly requestId: string;
  revision?: string;
};

// A retry key is not an authentication credential. Keep it with the selected
// photo in memory; a new selection (even the same URI) gets a new attempt.
export function createProfilePhotoAttempt(token: string): ProfilePhotoAttempt {
  return { token, requestId: createReservationRequestId().replace(/^reservation_/, 'photo_') };
}

// Sunucudan gelen hata (HTTP status'u taşır — ör. 402 yetersiz bakiye).
/** 9.33.9 — the member's active passkeys; `taskChannelReceiverId` only for the task bridge's senders. */
export type PasskeySummary = {
  id: string;
  label: string | null;
  backedUp: boolean;
  createdAt: string;
  lastUsedAt: string | null;
};
/** D-876 — a reset under way after a lost passkey, and whether its 48-hour wait is over. */
export type PasskeyRecovery = { readyAt: string; ready: boolean };
export type PasskeyList = { passkeys: PasskeySummary[]; taskChannelReceiverId?: number | null; recovery?: PasskeyRecovery | null; signIn?: boolean };
export type PasskeyConfirmationRequest =
  | { purpose: 'task_message'; receiver_id: number; type: string; content: string }
  | { purpose: 'add' }
  | { purpose: 'revoke'; passkey_id: string }
  | { purpose: 'recovery_cancel' };

export class ApiError extends Error {
  status: number;
  code: string | null;
  /** Sunucunun hata gövdesi — kodun yanında yapılandırılmış ayrıntı taşıdığında
   *  (ör. 3.10.5 fiyat damgası reddinde eski/yeni tutar) ekranın kullanacağı yer. */
  details: Record<string, any> | null;
  constructor(status: number, message: string, code?: string | null, details?: Record<string, any> | null) {
    super(message);
    this.status = status;
    this.code = code ?? null;
    this.details = details ?? null;
  }
}

const TIMEOUT_MS = 15000;
// Hesap paketi kullanıcının yaşam boyu kayıtlarını taşıyabilir. Normal ekran
// isteklerinin kısa sınırını büyütmeden yalnız bu açık kullanıcı eylemine daha
// geniş fakat yine sonlu bir pencere tanınır.
const ACCOUNT_EXPORT_TIMEOUT_MS = 120000;
// Fotoğraf gövdesi mobil ağda API'ye ulaşmadan önce yüklenir. Genel 15 saniyelik
// istek sınırı büyük iPhone fotoğraflarını yarıda kesebildiği için dosya aktarımı
// ayrı, fakat yine sonlu bir pencere kullanır.
const UPLOAD_TIMEOUT_MS = 60000;

// Sunucu geriye uyum kapılarını kişisel veri toplamadan ölçebilsin. Eski
// istemciler bu başlıkları göndermez; yeni istemciler yalnız sürüm ve platform
// ailesini bildirir, kullanıcı/cihaz kimliği taşımaz.
const CLIENT_HEADERS = {
  'X-Nosubject-Client-Version': Constants.expoConfig?.version ?? 'unknown',
  'X-Nosubject-Client-Platform': Platform.OS,
};

let unauthorizedHandler: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void) {
  unauthorizedHandler = fn;
}

let networkErrorHandler: (() => void) | null = null;
export function setNetworkErrorHandler(fn: () => void) {
  networkErrorHandler = fn;
}

/**
 * D-422 — 401 yalnız KİMLİKLİ isteklerde oturum bitişidir.
 *
 * Önceden her 401 oturum bitişi sayılıyordu: `unauthorizedHandler` token'ı
 * siliyor, durumu sıfırlıyor ve telefon ekranına atıyordu. Oysa `verify-otp`
 * **yanlış kod** için 401 döner ve o istekte henüz token yoktur. Sonuç: tek
 * harflik bir yazım hatası kullanıcıyı giriş akışının başına gönderiyor ve
 * ona "Oturum süresi doldu" deniyordu — ortada oturum bile yokken.
 *
 * Authorization başlığı gönderilmemiş bir istekte 401, oturumla ilgili
 * olamaz; o durumda `ApiError` fırlatılır ve anlamını ekran yorumlar.
 */
async function handleResponse<T>(res: Response, authenticated = true): Promise<T> {
  if (res.status === 401 && authenticated) {
    unauthorizedHandler?.();
    throw new AuthError();
  }
  // Sunucu HTML/boş gövde dönerse res.json() çökmesin
  let data: any = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  if (!res.ok) {
    throw new ApiError(res.status, data?.error ?? 'Bir hata oluştu', data?.code, data);
  }
  return data;
}

// D-910: the screen coordinator owns the whole pagination deadline and abort.
// Do not use safeFetch here: its independent signal would outlive a closed map.
export async function discoverMapRequest(token:string,after:number,signal:AbortSignal):Promise<unknown> {
  const res=await fetch(`${BASE_URL}/discover-locations?after=${after}`,{
    headers:{Authorization:token},signal,cache:'no-store',
  });
  return handleResponse(res);
}
export async function discoverMapInterest(token:string,ids:number[],signal:AbortSignal):Promise<readonly number[]> {
  const res=await fetch(`${BASE_URL}/discover-locations/interest`,{
    method:'POST',headers:{Authorization:token,'Content-Type':'application/json'},
    body:JSON.stringify({ids}),signal,cache:'no-store',
  });
  const value=await handleResponse<{ids:number[]}>(res);
  if(!value||!Array.isArray(value.ids))throw Error('Invalid map interest');
  return value.ids;
}

function handleUploadResponse<T>(status: number, body: string): T {
  let data: any = null;
  try {
    data = body ? JSON.parse(body) : null;
  } catch {
    data = null;
  }
  if (status === 401) {
    unauthorizedHandler?.();
    throw new AuthError();
  }
  if (status < 200 || status >= 300) {
    throw new ApiError(status, data?.error ?? 'Bir hata oluştu', data?.code, data);
  }
  return data as T;
}

async function safeFetch(input: string, init?: RequestInit, timeoutMs = TIMEOUT_MS): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } catch {
    // Ağ hatası veya zaman aşımı → banner tetikle
    networkErrorHandler?.();
    throw new NetworkError();
  } finally {
    clearTimeout(timer);
  }
}

async function get<T>(path: string): Promise<T> {
  const res = await safeFetch(`${BASE_URL}${path}`);
  return handleResponse<T>(res, false);
}

async function getAuth<T>(path: string, token: string, timeoutMs = TIMEOUT_MS): Promise<T> {
  const res = await safeFetch(`${BASE_URL}${path}`, {
    headers: {
      Authorization: token,
      'Cache-Control': 'no-store',
      Pragma: 'no-cache',
    },
  }, timeoutMs);
  return handleResponse<T>(res);
}

async function post<T>(path: string, body: object, token?: string): Promise<T> {
  const res = await safeFetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...CLIENT_HEADERS,
      ...(token ? { Authorization: token } : {}),
    },
    body: JSON.stringify(body),
  });
  return handleResponse<T>(res, token != null);
}

async function put<T>(path: string, body: object, token: string): Promise<T> {
  const res = await safeFetch(`${BASE_URL}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: token },
    body: JSON.stringify(body),
  });
  return handleResponse<T>(res);
}

async function del<T>(path: string, token?: string | null, body?: object): Promise<T> {
  const res = await safeFetch(`${BASE_URL}${path}`, {
    method: 'DELETE',
    headers: {
      ...(token ? { Authorization: token } : {}),
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  return handleResponse<T>(res, token != null);
}

async function patch<T>(path: string, body: object, token: string): Promise<T> {
  const res = await safeFetch(`${BASE_URL}${path}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: token },
    body: JSON.stringify(body),
  });
  return handleResponse<T>(res);
}

async function uploadFile<T>(
  path: string,
  token: string,
  uri: string,
  fieldName: string,
  webFile?: Blob,
  parameters?: Record<string, string>
): Promise<T> {
  const filename = uri.split('/').pop() ?? 'photo.jpg';
  // Bu değer yalnız multipart taşıma ipucudur. Sunucu D-142 gereği gerçek türü
  // dosya imzasından belirler; iOS Photos'un özgün HEIC adını dönüştürülmüş cache
  // URI'sine takmak native ağ katmanında uyumsuzluk çıkarabildiğinden kullanılmaz.
  const type = filename.endsWith('.png') ? 'image/png' : 'image/jpeg';

  // Web'de Expo FileSystem'ın upload API'si yok; tarayıcının FormData yolu korunur.
  if (Platform.OS === 'web') {
    const form = new FormData();
    // Expo ImagePicker web'de gerçek bir File verir. React Native'in
    // {uri,name,type} nesnesi tarayıcı FormData'sında dosya değil
    // "[object Object]" metni olur ve multer fotoğraf göremez.
    const blob = webFile ?? await fetch(uri).then((response) => response.blob());
    const webFilename = (webFile as Blob & { name?: string } | undefined)?.name ?? filename;
    // React Native'in FormData tipi üçüncü (filename) parametresini tanımlamıyor;
    // bu dal yalnız browser standardında çalışıyor ve parametre orada geçerli.
    (form.append as any)(fieldName, blob, webFilename);
    for (const [key, value] of Object.entries(parameters ?? {})) form.append(key, value);
    const res = await safeFetch(`${BASE_URL}${path}`, {
      method: 'POST',
      headers: { Authorization: token },
      body: form,
    }, UPLOAD_TIMEOUT_MS);
    return handleResponse<T>(res);
  }

  // iOS/Android'de dosyayı React Native FormData/fetch köprüsünden geçirmek yerine
  // native FileSystem aktarır. Photos/cache URI'sini doğrudan native katman okur;
  // istek sunucuya ulaşmadan oluşan "Network request failed" sınıfını kapatır.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);
  let result: Awaited<ReturnType<File['upload']>>;
  try {
    result = await new File(uri).upload(`${BASE_URL}${path}`, {
      httpMethod: 'POST',
      uploadType: UploadType.MULTIPART,
      fieldName,
      mimeType: type,
      parameters,
      headers: { Authorization: token },
      sessionType: 'foreground',
      signal: controller.signal,
    });
  } catch {
    networkErrorHandler?.();
    throw new NetworkError();
  } finally {
    clearTimeout(timer);
  }
  return handleUploadResponse<T>(result.status, result.body);
}

/**
 * Doğrudan yükleme (9.2.4 · D-563).
 *
 * Baytlar imzalı adrese **native aktarımla** gidiyor, RN'in fetch köprüsüyle
 * değil — köprü bu uygulamada "Network request failed" sınıfı hatalar üretiyor
 * ve 1 GB'lık bir dosyada bu çok daha olası. Aynı sebeple fotoğraf yüklemesi de
 * zaten `File.upload` kullanıyor.
 */
async function putBinary(uri: string, uploadUrl: string, contentType: string): Promise<number> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);
  try {
    const result = await new File(uri).upload(uploadUrl, {
      httpMethod: 'PUT',
      uploadType: UploadType.BINARY_CONTENT,
      headers: { 'Content-Type': contentType },
      sessionType: 'foreground',
      signal: controller.signal,
    });
    return result.status;
  } catch {
    networkErrorHandler?.();
    throw new NetworkError();
  } finally {
    clearTimeout(timer);
  }
}

export const api = {
  getVisitInvitations: (token:string, contactId:number) =>
    getAuth<import('../components/VisitInvitation').VisitSnapshot>(`/visit-invitations/${contactId}`, token),
  saveVisitInvitation: (token:string, contactId:number, edit:{revision:number;enabled:boolean;place?:string;timing?:string}) =>
    put<import('../components/VisitInvitation').VisitRecord>(`/visit-invitations/${contactId}`, edit, token),
  getGoogleSignInStatus: (token: string) =>
    getAuth<{ linked: boolean; linkedAt: string | null; linkedCount?: number; links?: unknown }>('/auth/google/status', token),
  // D-871 — removes one linked Google account; the proof is the fresh SMS-bound one.
  unlinkGoogle: (proof: string, linkId: string) => post<{ unlinked: true }>('/auth/google/unlink', { link: linkId }, proof),
  startGoogleReauthentication: (token: string) =>
    post<{ attempt: string; expiresAt: string; phoneHint: string }>('/auth/google/reauth/attempt', {}, token),
  completeGoogleReauthentication: (token: string, attempt: string, code: string) =>
    post<{ token: string }>('/auth/google/reauth', { attempt, code }, token),
  createGoogleLoginAttempt: () => post<GoogleAttempt>('/auth/google/login/attempt', {}),
  completeGoogleLogin: (attempt: string, idToken: string) =>
    post<GoogleSession>('/auth/google/login', { attempt, idToken }),
  createGoogleLinkAttempt: (token: string) => post<GoogleAttempt>('/auth/google/link/attempt', {}, token),
  completeGoogleLink: (token: string, attempt: string, idToken: string) =>
    post<{ linked: true }>('/auth/google/link', { attempt, idToken }, token),
  // 9.33.9 / D-780 — passkeys: list, add, remove, and the confirmation a task message
  // carries. The options and answers are WebAuthn JSON, passed through untouched.
  listPasskeys: (token: string) => getAuth<PasskeyList>('/passkeys', token),
  passkeyConfirmationOptions: (token: string, request: PasskeyConfirmationRequest) =>
    post<object>('/passkeys/confirmation/options', request, token),
  passkeyRegistrationOptions: (token: string, proof: { confirmation?: object; reauth?: string; recovery?: true }) =>
    post<object>('/passkeys/registration/options', proof, token),
  registerPasskey: (token: string, response: object, label: string, recovery = false) =>
    post<{ id: string }>('/passkeys/registration', { response, label, ...(recovery ? { recovery: true } : {}) }, token),
  // D-876 — reset after a lost passkey: start the 48-hour wait, or stop it with a passkey.
  requestPasskeyRecovery: (token: string, reauth: string) =>
    post<{ readyAt: string; created: boolean }>('/passkeys/recovery', { reauth }, token),
  cancelPasskeyRecovery: (token: string, confirmation: object) =>
    post<{ ok: true }>('/passkeys/recovery/cancel', { confirmation }, token),
  revokePasskey: (token: string, id: string, confirmation: object) =>
    post<{ ok: true }>(`/passkeys/${encodeURIComponent(id)}/revoke`, { confirmation }, token),
  // D-882 — signing in with a passkey, before there is a session.
  passkeySignInStatus: () => get<{ enabled: boolean }>('/passkeys/login/status'),
  passkeySignInOptions: () => post<object>('/passkeys/login/options', {}),
  passkeySignIn: (response: object) => post<{ token: string; onboarded: boolean }>('/passkeys/login', { response }),
  startPasskeyReauth: (token: string) =>
    post<{ attempt: string; expiresAt: string; phoneHint: string }>('/passkeys/reauth/attempt', {}, token),
  completePasskeyReauth: (token: string, attempt: string, code: string) =>
    post<{ ok: true }>('/passkeys/reauth', { attempt, code }, token),
  sendOtp: (phone: string) =>
    post<{ ok: boolean }>('/auth/send-otp', { phone }),

  verifyOtp: (phone: string, code: string) =>
    post<{ token: string; onboarded: boolean }>('/auth/verify-otp', { phone, code }),

  registerPushToken: (
    token: string,
    pushToken: string,
    platform: string,
    provider: 'expo' | 'fcm',
    language: string,
    timeZone: string | null,
    supportsSystemCalls: boolean
  ) => post<{ ok: true; deletionKey: string }>(
    '/push/register',
    { token: pushToken, platform, provider, language, timeZone, supportsSystemCalls },
    token
  ),

  deletePushToken: (token: string | null, pushToken: string, deletionKey?: string) =>
    del<{ ok: true }>(
      '/push/register',
      token,
      { token: pushToken, ...(deletionKey ? { deletionKey } : {}) }
    ),

  getInstagramConnection: (token: string) =>
    getAuth<InstagramConnectionStatus>('/integrations/instagram', token),

  startInstagramAuthorization: (token: string, language?: string) =>
    post<{ authorizationUrl: string; expiresAt: string }>(
      '/integrations/instagram/authorization',
      { language },
      token
    ),

  getGoogleCalendarConnection: (token: string) =>
    getAuth<GoogleCalendarConnectionStatus>('/integrations/calendar/google', token),

  startGoogleCalendarAuthorization: (token: string, language: string) =>
    post<{ authorizationUrl: string; expiresAt: string }>(
      '/integrations/calendar/google/authorization',
      { language },
      token
    ),

  getMicrosoftCalendarConnection: (token: string) =>
    getAuth<MicrosoftCalendarConnectionStatus>('/integrations/calendar/microsoft', token),

  startMicrosoftCalendarAuthorization: (token: string, language: string) =>
    post<{ authorizationUrl: string; expiresAt: string }>(
      '/integrations/calendar/microsoft/authorization',
      { language },
      token
    ),

  syncMicrosoftCalendar: (token: string, force = false) =>
    post<{ connected: boolean; skipped: boolean; calendars?: number; selectedCalendars?: number; changedEvents?: number }>(
      '/integrations/calendar/microsoft/sync',
      { force },
      token
    ),

  // Deliberately no disconnectMicrosoftCalendar: see MicrosoftCalendarConnectionStatus.

  disconnectGoogleCalendar: (token: string) =>
    del<{ ok: true; disconnected: number }>('/integrations/calendar/google', token),

  syncGoogleCalendar: (token: string, force = false) =>
    post<{ connected: boolean; skipped: boolean; calendars?: number; selectedCalendars?: number; changedEvents?: number }>(
      '/integrations/calendar/google/sync',
      { force },
      token
    ),

  selectGoogleCalendar: (token: string, calendarId: string, selected: boolean) =>
    patch<{ ok: true; selected: boolean }>(
      `/integrations/calendar/google/calendars/${encodeURIComponent(calendarId)}`,
      { selected },
      token
    ),

  getGoogleCalendarEvents: (
    token: string,
    range: { start: string; end: string; startDate: string; endDate: string }
  ) => getAuth<{ items: GoogleCalendarEvent[] }>(
    `/integrations/calendar/google/events?${new URLSearchParams(range).toString()}`,
    token
  ),

  // 9.26 / D-640 — uygulamanın dili; sunucu duyuruyu bu dilde yazıyor.
  setMyLanguage: (token: string, language: string) =>
    put<{ language: string }>('/users/language', { language }, token),

  getNotificationPreferences: (token: string) =>
    getAuth<NotificationPreferences>('/push/preferences', token),

  updateNotificationPreferences: (
    token: string,
    preferences: NotificationPreferencePatch
  ) => patch<NotificationPreferences>('/push/preferences', preferences, token),

  getNotificationUnreadCount: (token: string) =>
    getAuth<{ count: number }>('/notifications/unread-count', token),

  getNotifications: (token: string) =>
    getAuth<{ items: NotificationEvent[]; unreadCount: number }>('/notifications', token),

  markNotificationRead: (token: string, id: string) =>
    post<{ ok: true }>(`/notifications/${encodeURIComponent(id)}/read`, {}, token),

  markAllNotificationsRead: (token: string) =>
    post<{ ok: true }>('/notifications/read-all', {}, token),

  saveProfile: (token: string, name: string, surname?: string, universityId?: number, professionId?: number, prices?: { message?: number; voice?: number; call?: number; scheduledCall?: number; videoCall?: number; time?: number }, bio?: string, currency?: string, employerUsername?: string) =>
    patch<{
      id: number;
      name: string;
      surname: string | null;
      price_message: number;
      price_voice: number;
      price_call: number;
      price_call_scheduled: number;
      price_call_video: number;
      price_time: number;
      currency: string;
      bio: string | null;
      show_read_receipts: boolean;
      who_can_message: string;
      keep_archived: boolean;
      employer: ProfileEmployer | null;
    }>('/users/profile', {
      name,
      surname,
      university_id: universityId,
      profession_id: professionId,
      price_message: prices?.message,
      price_voice: prices?.voice,
      price_call: prices?.call,
      price_call_scheduled: prices?.scheduledCall,
      price_call_video: prices?.videoCall,
      price_time: prices?.time,
      bio,
      currency,
      employer_username: employerUsername,
    }, token),

  // ── Sesli arama (9.4) ────────────────────────────────────────────────
  // İlk dilim: ön planda, mevcut sohbet içinde, yalnız ses (9.4.13). Gerçek
  // zamanlı kanal olmadığı için sinyalleşme REST üzerinden yürüyor (9.4.5.1):
  // teklif çağrıyı açarken, cevap kabul ederken taşınıyor; iki taraf da durumu
  // yoklayarak öğreniyor. Push (9.26.1) gelince gelen-çağrı yoklaması sökülecek.
  getIceServers: (token: string) =>
    getAuth<{ iceServers: { urls: string; username?: string; credential?: string }[]; turnConfigured: boolean; ringTimeoutSeconds: number }>('/calls/ice', token),

  // 9.31.8 — `reservationId` verilirse çağrı o planlı rezervasyona bağlanır;
  // sunucu bağlanamıyorsa isteği reddeder (sessizce bağsız kaydetmez).
  startCall: (
    token: string,
    calleeId: number,
    offerSdp: string,
    clientRequestId: string,
    reservationId?: number | null,
    /** 3.10.5 — kullanıcıya GÖSTERİLEN dakika ücreti. Yalnız anlık aramada
     *  değerlendirilir; planlı çağrının ücreti teklifte donmuştur. */
    priceStamp?: { amount: number; currency: string } | null,
    /** 9.4.14 — sesli mi görüntülü mü. Sunucu tanımadığı değeri reddeder. */
    kind: 'audio' | 'video' = 'audio'
  ) =>
    post<Call>('/calls', {
      callee_id: calleeId,
      offer_sdp: offerSdp,
      kind,
      ...(reservationId ? { reservation_id: reservationId } : {}),
      client_request_id: clientRequestId,
      ...(priceStamp ? { price_stamp: priceStamp } : {}),
    }, token),

  // 3.10.5 — anlık aramanın dakika ücreti. Damga bu ucun verdiği tutarla gider.
  getCallPrice: (token: string, otherId: number) =>
    getAuth<CallPriceQuote>(`/calls/price/${otherId}`, token),

  getScheduledCallPrice: (token: string, otherId: number) =>
    getAuth<CallPriceQuote>(`/calls/price/${otherId}?mode=scheduled`, token),

  // 9.4.14 — görüntülü aramanın kendi dakika ücreti. Ayrı uç değil, aynı ucun
  // ayrı kipi: anlık ve görüntülü fiyat tek çözümleyiciden geliyor (D-511),
  // böylece kişiye özel fiyat iki kipte ayrışamıyor.
  getVideoCallPrice: (token: string, otherId: number) =>
    getAuth<CallPriceQuote>(`/calls/price/${otherId}?mode=video`, token),

  getIncomingCall: (token: string) =>
    getAuth<{ call: IncomingCall | null }>('/calls/incoming', token),

  getCall: (token: string, id: number) =>
    getAuth<Call & { remaining_seconds: number | null; ending_soon: boolean }>(`/calls/${id}`, token),

  acceptCall: (token: string, id: number, answerSdp: string) =>
    post<Call>(`/calls/${id}/accept`, { answer_sdp: answerSdp }, token),

  declineCall: (token: string, id: number) =>
    post<{ ok: true }>(`/calls/${id}/decline`, {}, token),

  endCall: (
    token: string,
    id: number,
    reason?: 'media_not_connected',
    interruptedSeconds?: number,
  ) =>
    post<{ ok: true; status: Call['status']; seconds: number; chargedAmount: number; receiverAmount: number }>(
      `/calls/${id}/end`, {
        ...(reason ? { reason } : {}),
        ...(interruptedSeconds !== undefined
          ? { interrupted_seconds: interruptedSeconds }
          : {}),
      }, token
    ),

  // Kümülatif toplam tekrar gönderilebilir: ağ yanıtı kaybolursa aynı değer
  // ikinci kez kesinti yazmaz. Kapanış da aynı toplamı transaction'a taşır.
  reportCallInterruption: (token: string, id: number, totalSeconds: number) =>
    post<{ ok: true; seconds: number; totalSeconds: number }>(
      `/calls/${id}/interruption`, { total_seconds: totalSeconds }, token
    ),

  // 9.4.4.5 — çağrı-ortası yeniden anlaşma (ICE-restart). Arayan yeni offer koyar,
  // aranan cevabı koyar; her iki taraf turu getCall yoklamasındaki seq'ten ayırt
  // eder. Yalnız süren çağrıda ve yalnız ilgili tarafta kabul edilir.
  renegotiateCall: (token: string, id: number, offerSdp: string) =>
    post<{ ok: true; seq: number }>(`/calls/${id}/renegotiate`, { offer_sdp: offerSdp }, token),

  answerCallRenegotiation: (token: string, id: number, answerSdp: string) =>
    post<{ ok: true; seq: number }>(`/calls/${id}/renegotiate-answer`, { answer_sdp: answerSdp }, token),

  // 9.4.10 — çağrı geçmişi ve kazanç listesi (sayfalı, geriye doğru). `before`
  // önceki sayfanın `next` değeri; boşsa en yeniden başlar.
  // `filter` 9.4.10 / D-635 — cevapsız sekmesi. Süzgeç SUNUCUDA: liste imleçle
  // sayfalandığı için istemcide süzmek, son sayfada cevapsız yoksa sekmeyi boş
  // gösterirdi; oysa daha geride duruyor olabilir.
  getCallHistory: (
    token: string,
    before?: string | null,
    limit?: number,
    filter: CallHistoryFilter = 'all',
  ) => {
    const q = new URLSearchParams();
    if (before) q.set('before', before);
    if (limit) q.set('limit', String(limit));
    if (filter !== 'all') q.set('filter', filter);
    const s = q.toString();
    return getAuth<{ calls: CallHistoryItem[]; next: string | null }>(`/calls/history${s ? `?${s}` : ''}`, token);
  },

  completeOnboardingProfile: (token: string, profile: {
    name: string; surname?: string; university_id?: number; isced_level?: number; profession_id?: number;
  }) => post<{ ok: true }>('/users/onboarding-profile', profile, token),

  // Eğitim (serbest liste) — ekle / sil. ISCED seviyesi: 5/6/7/8.
  addEducation: (token: string, universityId: number, iscedLevel: number, lang?: string) =>
    post<Education>(`/users/education${lang ? `?lang=${lang}` : ''}`, { university_id: universityId, isced_level: iscedLevel }, token),
  deleteEducation: (token: string, id: number) =>
    del<{ ok: boolean }>(`/users/education/${id}`, token),
  // Meslek araması (ESCO) — kayıt/profil picker'ı. q<2 → boş liste döner.
  // lang belirtilirse hem İngilizce hem o dilin çevirisinde arar, o dilde döner.
  searchProfessions: (q: string, lang?: string) =>
    get<Profession[]>(`/professions?q=${encodeURIComponent(q)}${lang ? `&lang=${lang}` : ''}`),

  // Meslek (serbest liste) — ekle / sil.
  addProfession: (token: string, professionId: number, lang?: string) =>
    post<UserProfession>(`/users/profession${lang ? `?lang=${lang}` : ''}`, { profession_id: professionId }, token),
  deleteProfession: (token: string, id: number) =>
    del<{ ok: boolean }>(`/users/profession/${id}`, token),

  // #34 kullanıcı adı (handle)
  checkUsername: (username: string) =>
    get<{ available: boolean; reason?: string }>(
      `/users/username-available/${encodeURIComponent(username)}`
    ),

  setUsername: (token: string, username: string) =>
    put<{ id: number; username: string }>('/users/username', { username }, token),

  markConversationUnread: (token: string, otherId: number) =>
    post<{ ok: boolean }>(`/messages/unread/${otherId}`, {}, token),

  markConversationRead: (token: string, otherId: number) =>
    del<{ ok: boolean }>(`/messages/unread/${otherId}`, token),

  getArchivedUnread: (token: string) =>
    getAuth<{ hasUnread: boolean }>('/messages/archived-unread', token),

  deleteAccount: (token: string) =>
    del<{ deletion_scheduled_at: string }>('/users/account', token),

  restoreAccount: (token: string) =>
    post<{ ok: boolean }>('/users/account/restore', {}, token),

  exportData: (token: string) =>
    getAuth<Record<string, unknown>>(
      '/users/account/export', token, ACCOUNT_EXPORT_TIMEOUT_MS
    ),

  acceptTerms: (token: string) =>
    post<{ ok: boolean }>('/users/accept-terms', {}, token),

  uploadPhoto: async (token: string, uri: string, webFile?: Blob, attempt = createProfilePhotoAttempt(token)) => {
    if (attempt.token !== token) throw new ApiError(409, 'photo_upload_conflict', 'photo_upload_conflict');
    if (attempt.revision === undefined) {
      const state = await getAuth<{ revision: string }>('/upload/photo-state', token);
      if (!state || typeof state.revision !== 'string' || !/^(0|[1-9][0-9]{0,18})$/.test(state.revision)) {
        throw new ApiError(502, 'invalid_photo_state');
      }
      attempt.revision = state.revision;
    }
    const send = () => uploadFile<{ photo_url: string }>('/upload/photo', token, uri, 'photo', webFile, {
      request_id: attempt.requestId, expected_revision: attempt.revision!,
    });
    try { return await send(); } catch (error) {
      // Only one transport retry, using exactly the original revision and key.
      // Never refresh the base revision after ambiguity or fall back to legacy.
      if (!(error instanceof NetworkError)) throw error;
      return send();
    }
  },

  uploadMessagePhoto: (
    token: string,
    uri: string,
    webFile?: Blob,
    coordinates?: { latitude: number; longitude: number },
    language?: string,
    locationSource: 'file_metadata' | 'capture_location' = 'file_metadata'
  ) => uploadFile<{
    url: string;
    location: { district: string | null; city: string; source: 'file_metadata' | 'capture_location' } | null;
  }>('/upload/message-photo', token, uri, 'photo', webFile, coordinates ? {
    latitude: String(coordinates.latitude),
    longitude: String(coordinates.longitude),
    location_source: locationSource,
    ...(language ? { language } : {}),
  } : undefined),

  // 1.3.7 / D-665 — giriş yapmış üye oturumunu gönderir. Göndermediği için
  // sunucu onu hesabı olmayan biri sanıyordu: engellenen kişiler keşifte
  // kalıyordu ve "Kimse" diyen üyeler üyelerden de gizlenirdi.
  discover: (q?: string, lang?: string, token?: string | null, group?: string) => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (lang) params.set('lang', lang);
    if (group) params.set('group', group);
    const qs = params.toString();
    const path = qs ? `/discover?${qs}` : '/discover';
    return token ? getAuth<DiscoverUser[]>(path, token) : get<DiscoverUser[]>(path);
  },

  // 8.6 / D-667 — platformun en yeni derlemesi ve kurulum linki (herkese açık).
  getLatestRelease: (platform: 'ios' | 'android') =>
    get<{ platform: string; latest_version: string; install_url: string }>(`/app/version?platform=${platform}`),

  // 1.3.7 / D-665 — oturum varsa gönderilir: "Kimse" diyen üyenin profili
  // yalnız hesabı olmayanlardan gizlenir, üyelerden değil.
  getUser: (id: number, lang?: string, token?: string | null) => {
    const path = `/users/${id}${lang ? `?lang=${lang}` : ''}`;
    return token ? getAuth<UserProfile>(path, token) : get<UserProfile>(path);
  },

  // 9.35.3 · D-830 — profile photos and videos. Another member's list follows
  // the profile's visibility on the server; the session is sent when present.
  // Paged since 16 October 2026: `cursor` is the `next_cursor` of the page before.
  getMyProfileMedia: (token: string, cursor?: string | null) =>
    getAuth<{ items: ProfileMediaItem[]; next_cursor?: string | null; total?: number; limit: number }>(
      `/profile-media/mine${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`, token),
  getProfileMedia: (userId: number, token?: string | null, cursor?: string | null) => {
    const path = `/profile-media/users/${userId}${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`;
    return token
      ? getAuth<{ items: ProfileMediaItem[]; next_cursor?: string | null; total?: number }>(path, token)
      : get<{ items: ProfileMediaItem[]; next_cursor?: string | null; total?: number }>(path);
  },
  /** Archive paths already on one's own profile, for the importer; no read links are made. */
  getMyProfileMediaKeys: (token: string) =>
    getAuth<{ keys: string[]; count: number; limit: number }>('/profile-media/mine/keys', token),
  // 9.18.10.4 · D-830 — puts one verified profile upload on the profile.
  attachProfileMedia: (token: string, input: { url: string; sourceKey: string; sourceTakenAt: string | null }) =>
    post<ProfileMediaItem>('/profile-media', {
      url: input.url,
      source: 'instagram_archive',
      source_key: input.sourceKey,
      ...(input.sourceTakenAt ? { source_taken_at: input.sourceTakenAt } : {}),
    }, token),
  // 9.35.4 · D-830 — removes one of one's own items; the server deletes the file.
  removeProfileMedia: (token: string, id: string) =>
    del<void>(`/profile-media/${encodeURIComponent(id)}`, token),

  // 1.3.5 / D-649 — paylaşılan linkteki ad → kimlik. Giriş yapmış üye adına:
  // hesapsız biri bu uçla üye adlarını yoklayamaz.
  getUserIdByUsername: (token: string, username: string) =>
    getAuth<{ id: number; username: string }>(`/users/by-username/${encodeURIComponent(username)}`, token),

  // Kendi profili gizlilik/hesap alanlarını da taşır. Genel profil çağrısı
  // kimliksiz kalmalı; sunucu show_read_receipts, keep_archived ve benzeri
  // özel alanları yalnız Authorization sahibine döndürüyor (D-111).
  getOwnUser: (token: string, id: number, lang?: string) =>
    getAuth<UserProfile>(`/users/${id}${lang ? `?lang=${lang}` : ''}`, token),

  getProfilePreferences: (token: string) =>
    getAuth<ProfilePreferences>('/users/preferences', token),

  updateProfilePreferences: (token: string, preferences: Partial<ProfilePreferences>) =>
    patch<ProfilePreferences>('/users/preferences', preferences, token),

  getWallet: (token: string) =>
    getAuth<WalletData>('/wallet', token),

  topUpWallet: (token: string, amount: number, currency: string) =>
    post<{ balance: number }>('/wallet/topup', { amount, currency }, token),

  requestPayout: (token: string, amount: number) =>
    post<PayoutRequest>('/wallet/payout-request', { amount }, token),

  getPayoutRequests: (token: string) =>
    getAuth<PayoutRequest[]>('/wallet/payout-requests', token),

  startStripeOnboarding: (token: string) =>
    post<{ url: string }>('/stripe/connect/onboard', {}, token),

  getStripeStatus: (token: string) =>
    getAuth<{ connected: boolean; onboarded: boolean; payoutsEnabled?: boolean }>('/stripe/connect/status', token),

  // deviceId — 9.34 / D-628: önizleme de bu cihazın zarfından okunmalı.
  // Verilmezse sunucu eski alana düşer; sahadaki sürümler için geçerli durum.
  getConversations: (token: string, archived: boolean = false, deviceId?: number) => {
    const q = [archived ? 'archived=1' : '', deviceId ? `device_id=${deviceId}` : '']
      .filter(Boolean)
      .join('&');
    return getAuth<Conversation[]>(`/messages/conversations${q ? `?${q}` : ''}`, token);
  },

  archiveConversation: (token: string, otherId: number) =>
    post<{ ok: boolean }>(`/messages/archive/${otherId}`, {}, token),

  unarchiveConversation: (token: string, otherId: number) =>
    del<{ ok: boolean }>(`/messages/archive/${otherId}`, token),

  // durationHours > 0 → süreli sustur; verilmezse süresiz. (#7)
  muteConversation: (token: string, otherId: number, durationHours?: number) =>
    post<{ ok: boolean }>(`/messages/mute/${otherId}`, { durationHours: durationHours ?? 0 }, token),

  unmuteConversation: (token: string, otherId: number) =>
    del<{ ok: boolean }>(`/messages/mute/${otherId}`, token),

  blockUser: (token: string, otherId: number) =>
    post<{ ok: boolean }>(`/messages/block/${otherId}`, {}, token),

  unblockUser: (token: string, otherId: number) =>
    del<{ ok: boolean }>(`/messages/block/${otherId}`, token),

  // D-305 — tek kişinin engel durumu. Yalnız KENDİ engelimi bildirir; karşı
  // tarafın beni engellediği bilgisi sunucudan hiç gelmez (gizlilik).
  getBlockStatus: (token: string, otherId: number) =>
    getAuth<{ blocked: boolean }>(`/messages/block/${otherId}`, token),

  getBlocked: (token: string) =>
    getAuth<BlockedUser[]>('/messages/blocked', token),

  // 9.4.7.5 — kişi bazlı arama istisnaları listesi (D-227). Yalnız istisna
  // konmuş kişiler döner; varsayılandakiler listede yer almaz.
  getCallExceptions: (
    token: string,
    options: { limit?: number; cursor?: string; query?: string } = {},
  ) => {
    const params = new URLSearchParams({ limit: String(options.limit ?? 30) });
    if (options.cursor) params.set('cursor', options.cursor);
    if (options.query?.trim()) params.set('q', options.query.trim());
    return getAuth<ContactExceptionPage<CallException>>(
      `/messages/call-exceptions?${params.toString()}`,
      token,
    );
  },

  getMessageExceptions: (
    token: string,
    options: { limit?: number; cursor?: string; query?: string } = {},
  ) => {
    const params = new URLSearchParams({ limit: String(options.limit ?? 30) });
    if (options.cursor) params.set('cursor', options.cursor);
    if (options.query?.trim()) params.set('q', options.query.trim());
    return getAuth<ContactExceptionPage<MessageException>>(
      `/messages/message-exceptions?${params.toString()}`,
      token,
    );
  },

  // `deviceId` verilirse sunucu BU cihazın zarfını döndürür (9.34 · D-624).
  // Verilmezse bugünkü tek-zarflı alan gelir; eski sürümler öyle çalışıyor.
  getMessageStars: (token: string, otherId: number) =>
    getAuth<{ messageIds: number[] }>(`/messages/stars/${otherId}`, token),
  setMessageStar: (token: string, messageId: number, starred: boolean) =>
    put<{ starred: boolean }>(`/messages/${messageId}/star`, { starred }, token),
  getMessages: (token: string, otherId: number, deviceId?: number) =>
    getAuth<Message[]>(
      `/messages/${otherId}${deviceId != null ? `?device_id=${deviceId}` : ''}`, token),

  // 3.10.5 — sohbette gösterilen mesaj fiyatı. Damga bu ucun verdiği tutarla
  // gönderilir; sunucu kesintiyi onunla sınırlar.
  getMessagePrice: (token: string, otherId: number) =>
    getAuth<MessagePriceQuote>(`/messages/price/${otherId}`, token),

  /** 9.2.4 — yükleme bileti; beyan yalnız adres üretmek için, kabul kararı değil. */
  createMediaTicket: (
    token: string,
    input: { kind: string; contentType: string; sizeBytes: number; durationSeconds?: number; purpose?: 'profile' }
  ) => post<{ upload_url: string; url: string }>('/upload/media-ticket', {
    kind: input.kind,
    content_type: input.contentType,
    size_bytes: input.sizeBytes,
    ...(input.durationSeconds !== undefined ? { duration_seconds: input.durationSeconds } : {}),
    // 9.35.1 · D-830 — a profile upload can only become a profile item.
    ...(input.purpose ? { purpose: input.purpose } : {}),
  }, token),

  /** Yükleme bitti: sunucu nesnenin boyutunu ve imzasını okuyup damgalıyor. */
  finalizeMedia: (token: string, input: { kind: string; url: string }) =>
    post<{ url: string; mime_type: string; size_bytes: number }>('/upload/media-finalize', {
      kind: input.kind, url: input.url,
    }, token),

  /**
   * 9.1.9 — ses kaydı ısmarla. Para sipariş ANINDA emanete giriyor, yani bu
   * çağrı başarılıysa bakiye çoktan azalmıştır; ekran cüzdanı tazelemeli.
   * Bakiye yetmezse sunucu 402 döner.
   */
  createVoiceOrder: (token: string, sellerId: number, seconds: number) =>
    post<VoiceOrderCreated>('/voice-orders', { seller_id: sellerId, seconds }, token),

  /**
   * 9.1.11 — teslim. Kayıt genel medya yolundan yüklenmiş olmalı; burada
   * yapılan onu siparişe bağlamak. Süreyi İSTEMCİ BİLDİRMİYOR: sunucu kaptan
   * ölçüyor (D-572), çünkü burada uzun beyan etmek satıcıya para kazandırırdı.
   */
  deliverVoiceOrder: (token: string, orderId: number, url: string) =>
    post<VoiceOrderDelivered>(`/voice-orders/${orderId}/deliver`, { url }, token),

  /** 9.1.12 — reddet. Emanetin tamamı ısmarlayana döner, komisyon kesilmez. */
  declineVoiceOrder: (token: string, orderId: number) =>
    post<{ order_id: number; status: string; refunded: number }>(
      `/voice-orders/${orderId}/decline`, {}, token,
    ),

  // Signal Protokolü anahtar dizini (9.34 · D-603, D-604). Cihaz KENDİ AÇIK
  // anahtarlarını yükler; özel anahtarlar cihazdan çıkmaz. Yalnız açık materyal
  // ve imza gönderilir.
  uploadKeyBundle: (token: string, bundle: KeyBundleUploadBody) =>
    put<KeyBundleUploadResult>('/keys', bundle, token),

  // Cihaz numarası verilmezse sunucu EN SON kullanılan cihazı sayar; kendi
  // numarasını bilen istemci onu geçmeli, yoksa başka bir cihazın deposuna
  // bakmış olur (9.34 · D-618).
  keyBundleCount: (token: string, deviceId?: number) =>
    getAuth<{ one_time_prekeys: number; device_id: number | null }>(
      `/keys/count${deviceId != null ? `?device_id=${deviceId}` : ''}`, token),

  // Bir kullanıcıyla oturum başlatmak için prekey demeti al. Sunucuda bir
  // tek-kullanımlık prekey tükenir; demet bittiyse `one_time_prekey` null döner.
  fetchKeyBundle: (token: string, userId: number) =>
    getAuth<PrekeyBundle>(`/keys/${userId}`, token),

  // Bir kullanıcının TÜM cihazlarının demetleri (9.34 · D-622). Fan-out bunu
  // kullanıyor: mesaj her cihaz için ayrı şifreleniyor.
  fetchAllKeyBundles: (token: string, userId: number) =>
    getAuth<{ user_id: number; devices: PrekeyBundle[] }>(`/keys/${userId}/all`, token),

  // 9.34 / D-630 — "kimler var" ile "oturum kur" ayrı sorular. Liste prekey
  // TÜKETMEZ; demet yalnız oturumu olmayan cihaz için istenir.
  fetchDeviceList: (token: string, userId: number) =>
    getAuth<{ user_id: number; devices: DeviceIdentity[] }>(`/keys/${userId}/devices`, token),

  fetchDeviceKeyBundle: (token: string, userId: number, deviceId: number) =>
    getAuth<PrekeyBundle>(`/keys/${userId}/device/${deviceId}`, token),

  // 9.34 / D-600 — şifreli geçmiş yedeği. Sunucu blob'u saklar, açamaz.
  putBackup: (token: string, blob: EncryptedBackup) =>
    put<{ stored: boolean; bytes: number }>('/backup', blob, token),

  getBackup: (token: string) =>
    getAuth<EncryptedBackup & { updated_at: string }>('/backup', token),

  /** 9.3.3.2 — canlı konum paylaşımı. Süre verilmezse süresizdir (D-575). */
  startLocationShare: (token: string, viewerId: number, minutes: number | null) =>
    post<LocationShare>('/location-shares', { viewer_id: viewerId, minutes }, token),

  stopLocationShare: (token: string, shareId: number) =>
    post<{ id: number; status: string }>(`/location-shares/${shareId}/stop`, {}, token),

  activeLocationShare: (token: string, viewerId: number) =>
    getAuth<{ share: LocationShare | null }>(`/location-shares/active/${viewerId}`, token),

  /**
   * 9.3.3.4 — paylaşan nokta bildirir. Yanıt, ölçmeye devam edilip
   * edilmeyeceğini söylüyor: bakan yoksa cihaz duruyor.
   */
  sendLocationPoint: (
    token: string,
    shareId: number,
    point: { latitude: number; longitude: number; accuracyM?: number | null }
  ) => post<{ watching: boolean }>(`/location-shares/${shareId}/point`, {
    latitude: point.latitude,
    longitude: point.longitude,
    ...(point.accuracyM != null ? { accuracy_m: point.accuracyM } : {}),
  }, token),

  /**
   * 9.3.3.5 — izleyen konumu okur. Okumak aynı zamanda "bakıyorum" demek:
   * paylaşan cihazın ölçmeye devam edip etmeyeceğini bu belirliyor.
   */
  liveLocationPosition: (token: string, shareId: number) =>
    getAuth<{
      latitude: number | null; longitude: number | null;
      accuracy_m: number | null; point_at: string | null; expires_at: string | null;
    }>(`/location-shares/${shareId}/position`, token),

  uploadMediaBytes: putBinary,
  /**
   * 9.18.10.4 · D-830 — the web has no native upload: the browser sends the
   * Blob to the signed address itself. Returns the HTTP status like putBinary.
   */
  uploadBlobBytes: async (blob: Blob, uploadUrl: string, contentType: string): Promise<number> => {
    try {
      const response = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: blob });
      return response.status;
    } catch {
      networkErrorHandler?.();
      throw new NetworkError();
    }
  },

  editMessage: (token: string, messageId: number, body: {
    content: string; type: string; expected_revision: number;
    envelopes?: MessageEnvelope[]; link_preview?: LinkPreview | null;
  }) => patch<Message>(`/messages/${messageId}/content`, body, token),

  mediaForwardStatus:(token:string)=>getAuth<{enabled:boolean}>('/media-forward/status',token),
  prepareMediaForward:(token:string,body:{sourceId:number;sourceRevision:number;destinationId:number;requestId:string})=>
    post<ForwardMediaProgress>('/media-forward',body,token),
  advanceMediaForward:(token:string,operation:string)=>
    post<ForwardMediaProgress>(`/media-forward/${encodeURIComponent(operation)}/advance`,{},token),
  sendMessage: (
    token: string,
    receiverId: number,
    content: string,
    type: string = 'text',
    replyToId?: number,
    photoLocationVisible?: boolean,
    /** Kullanıcıya GÖSTERİLEN tutar. Kesinti bundan yüksekse sunucu 409 verir. */
    priceStamp?: { amount: number; currency: string } | null,
    /** 9.2.4 — yalnız medya mesajında; sunucu sınır dışı değeri reddediyor. */
    durationSeconds?: number,
    /** 9.34 · D-623 — cihaz başına şifreli kopyalar. Boşsa alan hiç gönderilmez. */
    envelopes?: MessageEnvelope[],
    /**
     * 9.32 · D-658 — yalnız ŞİFRESİZ düz metin mesajında: önizleme metinden ayrı
     * bir alanda gider. Şifreli mesajın önizlemesi zarfın içindedir; sunucu
     * şifreli mesajın yanında bu alanı reddeder.
     */
    linkPreview?: LinkPreview | null,
    caption?: { content: string; encrypted: boolean },
    /** 9.33.9.4 / D-780 — a passkey answer bound to this receiver, type and content. */
    passkeyConfirmation?: object,
    forwardOperation?: string,
  ) => post<Message>('/messages', {
    receiver_id: receiverId,
    content,
    type,
    reply_to_id: replyToId,
    ...(photoLocationVisible === undefined ? {} : { photo_location_visible: photoLocationVisible }),
    ...(priceStamp ? { price_stamp: priceStamp } : {}),
    ...(durationSeconds === undefined ? {} : { duration_seconds: durationSeconds }),
    ...(envelopes && envelopes.length ? { envelopes } : {}),
    ...(linkPreview ? { link_preview: linkPreview } : {}),
    ...(caption ? { caption } : {}),
    ...(passkeyConfirmation ? { passkey_confirmation: passkeyConfirmation } : {}),
    ...(forwardOperation ? {forward_operation:forwardOperation} : {}),
  }, token),

  updatePhotoLocationVisibility: (token: string, messageId: number, visible: boolean) =>
    patch<Pick<Message,
      'photo_location_district' | 'photo_location_city' | 'photo_location_source' | 'photo_location_visible'
    >>(`/messages/${messageId}/photo-location`, { visible }, token),

  cancelMessage: (token: string, messageId: number) =>
    del<{ ok: boolean }>(`/messages/${messageId}`, token),

  reactToMessage: (token: string, messageId: number, emoji: string) =>
    put<{ ok: boolean }>(`/messages/${messageId}/reaction`, { emoji }, token),

  removeReaction: (token: string, messageId: number) =>
    del<{ ok: boolean }>(`/messages/${messageId}/reaction`, token),

  // D-302 — kişiye özel fiyat yalnız mesaj değildir. `price` (mesaj başına)
  // zorunludur; diğer dört tür isteğe bağlıdır ve ÜÇ DURUM taşır:
  //   alan hiç gönderilmez → sunucudaki mevcut değer korunur
  //   null gönderilir      → o türdeki özel fiyat kaldırılır, genel fiyat geçerli
  //   sayı gönderilir      → yazılır (0 dahil: "bu kişiye ücretsiz")
  setContactPrice: (
    token: string,
    contactId: number,
    price: number,
    extras?: Partial<Record<ContactPriceKind, number | null>>,
  ) =>
    put<ContactPrices & { ok: boolean }>(
      `/messages/contact-price/${contactId}`,
      { price, ...(extras ?? {}) },
      token,
    ),

  getContactPrice: (token: string, contactId: number) =>
    getAuth<ContactPrices>(`/messages/contact-price/${contactId}`, token),

  // 3.1.6.3 — kişiye özel fiyatı kaldırır ve kişiyi "Kişilerim" listesinden
  // çıkarır; kişi senin genel fiyatına döner. Engelleme DEĞİLDİR (o ayrı yol).
  removeContactPrice: (token: string, contactId: number) =>
    del<{ ok: boolean; removed: boolean }>(`/messages/contact-price/${contactId}`, token),

  getContactPreferences: (token: string, contactId: number) =>
    getAuth<ContactPreferences>(`/messages/contact-preferences/${contactId}`, token),

  updateContactPreferences: (
    token: string,
    contactId: number,
    // Kısmi patch: yalnız gönderilen alan değişir; tercihler birbirinden
    // bağımsız ayarlanabilir.
    preferences: Partial<Pick<ContactPreferences,
      'translation_enabled' | 'translation_language' | 'call_allowed' | 'message_allowed'>>
  ) => patch<ContactPreferences>(`/messages/contact-preferences/${contactId}`, preferences, token),

  // D-693 — sinyal kime yazıldığını taşır; yalnız o kişi görür.
  sendTyping: (token: string, to: number) =>
    post<{ ok: boolean }>('/messages/typing', { to }, token),

  // 9.20.6 — gönderince ya da sohbetten çıkınca gösterge hemen düşsün.
  stopTyping: (token: string) =>
    post<{ ok: boolean }>('/messages/typing/stop', {}, token),

  getTyping: (token: string, otherId: number) =>
    getAuth<{ isTyping: boolean }>(`/messages/typing/${otherId}`, token),

  translateMessages: (token: string, messageIds: number[], target: string) =>
    post<{
      translations: Array<{
        message_id: number;
        text: string;
        detected_source: string;
        model: 'nmt' | 'translation-llm';
        cached: boolean;
      }>;
    }>('/messages/translate', { message_ids: messageIds, target }, token),

  getLanguages: (token: string, display: string) =>
    getAuth<{ languages: { code: string; name: string }[] }>(
      `/messages/languages?display=${encodeURIComponent(display)}`,
      token
    ),

  proposeMeetup: async (
    token: string,
    recipientId: number,
    proposedAt: string,
    location?: string,
    durationMinutes?: number,
    /** 3.10.5 — teklif ekranında gösterilen dakika ücreti, SATICININ para
     *  biriminde: teklif o ücreti dondurur (D-252). */
    priceStamp?: { amount: number; currency: string } | null
  ) => {
    const clientRequestId = createReservationRequestId();
    const body = {
      recipient_id: recipientId,
      proposed_at: proposedAt,
      source_time_zone: getDeviceTimeZone(),
      location,
      duration_minutes: durationMinutes,
      kind: 'meetup',
      client_request_id: clientRequestId,
      ...(priceStamp ? { price_stamp: priceStamp } : {}),
    };
    try {
      return await post<Meetup>('/meetups', body, token);
    } catch (error) {
      if (!(error instanceof NetworkError)) throw error;
      return post<Meetup>('/meetups', body, token);
    }
  },

  // `subject` (isteğe bağlı arama konusu) sunucuda `location` sütununda saklanır —
  // buluşmanın konum alanıyla aynı yer, tür'e göre anlam (D-104).
  proposeCallReservation: async (
    token: string,
    recipientId: number,
    proposedAt: string,
    durationMinutes: number,
    subject?: string,
    /** 3.10.5 — teklif ekranında gösterilen dakika ücreti, SATICININ para
     *  biriminde: teklif o ücreti dondurur (D-252). */
    priceStamp?: { amount: number; currency: string } | null
  ) => {
    const clientRequestId = createReservationRequestId();
    const body = {
      recipient_id: recipientId,
      proposed_at: proposedAt,
      source_time_zone: getDeviceTimeZone(),
      duration_minutes: durationMinutes,
      kind: 'call',
      location: subject,
      client_request_id: clientRequestId,
      ...(priceStamp ? { price_stamp: priceStamp } : {}),
    };
    try {
      return await post<Meetup>('/meetups', body, token);
    } catch (error) {
      if (!(error instanceof NetworkError)) throw error;
      return post<Meetup>('/meetups', body, token);
    }
  },

  getMeetups: (token: string, status?: MeetupStatus) =>
    getAuth<Meetup[]>(`/meetups${status ? `?status=${status}` : ''}`, token),

  getMeetupsPage: (
    token: string,
    options: {
      status?: MeetupStatus;
      from?: string;
      to?: string;
      limit?: number;
      cursor?: string;
      scope?: 'history';
    } = {},
  ) => {
    const params = new URLSearchParams();
    if (options.status) params.set('status', options.status);
    if (options.from) params.set('from', options.from);
    if (options.to) params.set('to', options.to);
    params.set('limit', String(options.limit ?? 100));
    if (options.cursor) params.set('cursor', options.cursor);
    if (options.scope) params.set('scope', options.scope);
    return getAuth<MeetupPage>(`/meetups?${params.toString()}`, token);
  },

  getMeetup: async (token: string, id: number) => {
    try {
      return await getAuth<Meetup>(`/meetups/${id}`, token);
    } catch (error) {
      // Dağıtım sırası boyunca eski API bu rotayı tanımayıp gövdesiz 404
      // döndürebilir. Yeni API'nin anlamlı "bulunamadı" yanıtını veya başka
      // bir hatayı gizleme; yalnız rota-yok geçişinde mevcut listeye düş.
      if (!(error instanceof ApiError)
        || error.status !== 404
        || error.message !== 'Bir hata oluştu') {
        throw error;
      }
      const meetups = await getAuth<Meetup[]>('/meetups', token);
      const meetup = meetups.find((item) => item.id === id);
      if (!meetup) throw error;
      return meetup;
    }
  },

  respondToMeetup: (
    token: string,
    id: number,
    action: 'accept' | 'decline',
    expectedRecordRevision?: number,
  ) => post<Meetup>(`/meetups/${id}/respond`, {
    action,
    expected_record_revision: expectedRecordRevision,
  }, token),

  cancelMeetup: (token: string, id: number, expectedRecordRevision?: number) =>
    post<Meetup>(`/meetups/${id}/cancel`, {
      expected_record_revision: expectedRecordRevision,
    }, token),

  proposeMeetupReschedule: (
    token: string,
    id: number,
    proposedAt: string,
    expectedRecordRevision?: number,
  ) =>
    post<MeetupRescheduleRequest>(
      `/meetups/${id}/reschedule`,
      {
        proposed_at: proposedAt,
        source_time_zone: getDeviceTimeZone(),
        expected_record_revision: expectedRecordRevision,
      },
      token
    ),

  respondToMeetupReschedule: (
    token: string,
    meetupId: number,
    requestId: number,
    action: 'accept' | 'decline',
    expectedRecordRevision?: number,
  ) => post<{ request: MeetupRescheduleRequest; meetup: Meetup }>(
    `/meetups/${meetupId}/reschedule/${requestId}/respond`,
    { action, expected_record_revision: expectedRecordRevision },
    token
  ),

  cancelMeetupReschedule: (
    token: string,
    meetupId: number,
    requestId: number,
    expectedRecordRevision?: number,
  ) =>
    post<MeetupRescheduleRequest>(
      `/meetups/${meetupId}/reschedule/${requestId}/cancel`,
      { expected_record_revision: expectedRecordRevision },
      token
    ),
};

// Buluşma teklifi (9.3.2) — GET /meetups yanıtı. `direction`/`other_name`/
// `other_photo` sunucu tarafında mevcut kullanıcıya göre hesaplanır.
export type MeetupStatus = 'pending' | 'accepted' | 'declined' | 'cancelled' | 'expired';
export type ReservationCallLinkState =
  | 'not_applicable'
  | 'not_accepted'
  | 'already_completed'
  | 'in_progress'
  | 'too_early'
  | 'available'
  | 'expired';

export type MeetupPage = {
  items: Meetup[];
  next_cursor: string | null;
};
export type NotificationEventType =
  | 'meetup_requested'
  | 'meetup_accepted'
  | 'meetup_declined'
  | 'meetup_cancelled'
  | 'meetup_reschedule_requested'
  | 'meetup_reschedule_accepted'
  | 'meetup_reschedule_declined'
  | 'meetup_reschedule_cancelled'
  | 'call_reservation_requested'
  | 'call_reservation_accepted'
  | 'call_reservation_declined'
  | 'call_reservation_cancelled'
  | 'call_reservation_reschedule_requested'
  | 'call_reservation_reschedule_accepted'
  | 'call_reservation_reschedule_declined'
  | 'call_reservation_reschedule_cancelled'
  // 9.28 — yaklaşan kabul edilmiş etkinliğin hatırlatması.
  | 'meetup_reminder'
  | 'call_reservation_reminder'
  | 'meetup_starting'
  | 'call_reservation_starting'
  | 'call_incoming'
  | 'call_missed'
  | 'escrow_expired_refunded'
  | 'escrow_released_earning';

// Para olayları Cüzdan'a gider. Hedefi `entity_type` ile seçmek yetmiyor:
// kazanç olayının varlığı CEVAP MESAJI, yani türü 'message'. Tek ölçüt varlık
// türü olsaydı kazanca dokunmak takvimi açardı (9.26.3.1.3).
export const FINANCIAL_NOTIFICATION_TYPES: NotificationEventType[] = [
  'escrow_expired_refunded',
  'escrow_released_earning',
];

export function isFinancialNotification(type: NotificationEventType): boolean {
  return FINANCIAL_NOTIFICATION_TYPES.includes(type);
}

export type NotificationEvent = {
  id: string;
  type: NotificationEventType;
  entity_type: 'meetup' | 'call' | 'transaction' | 'message';
  entity_id: number;
  conversation_message_id: number | null;
  read_at: string | null;
  created_at: string;
  expires_at: string;
  actor_id: number | null;
  actor_name: string | null;
  actor_photo: string | null;
  meetup_kind: 'meetup' | 'call' | null;
  proposed_at: string | null;
  meetup_status: MeetupStatus | null;
};

export type Meetup = {
  id: number;
  requester_id: number;
  recipient_id: number;
  proposed_at: string; // ISO 8601
  // Eski API revizyonuyla kısa dağıtım örtüşmesinde alan bulunmayabilir.
  source_time_zone?: string | null;
  location: string | null;
  duration_minutes: number;
  kind: 'meetup' | 'call';
  status: MeetupStatus;
  schedule_revision?: number;
  record_revision?: number;
  price_per_min?: number | string | null;
  price_currency?: string | null;
  total_amount?: number | string | null;
  pricing_frozen?: boolean;
  // 9.31.7 — planlı aramanın gerçekleşip gerçekleşmediğinin OLGUSAL sonucu.
  // null: pencere hâlâ açık ya da tür/durum uygun değil. Kimin haksız olduğu
  // bilgisi DEĞİLDİR; yalnız bağlı ücretli çağrının varlığını bildirir.
  call_outcome?: 'held' | 'not_held' | null;
  // Gerçekleşen bağlı çağrının izleyene göre mahsuplaşması. Rezervasyonun
  // teklif toplamından ayrıdır: çağrı gerçekten kullanılan saniyeye göre işler.
  call_settlement?: {
    billed_seconds: number;
    viewer_amount: number | string;
    viewer_currency: string;
    viewer_amount_kind: 'spent' | 'earned';
    settled_at: string;
  } | null;
  // Yalnız kanonik ayrıntı ucunda bulunur. Görünür düğme bu sunucu durumuna
  // göre seçilir; çağrı başlangıç ucu aynı koşulları yeniden denetler.
  call_link_state?: ReservationCallLinkState;
  call_link_opens_at?: string | null;
  call_link_closes_at?: string | null;
  // Kanonik ayrıntının üretildiği sunucu anı. Mobil, çağrı penceresi için cihaz
  // saatine güvenmek yerine istek gidiş/dönüş orta noktasından sapmayı hesaplar.
  server_now?: string;
  created_at: string;
  responded_at: string | null;
  accepted_at?: string | null;
  declined_at?: string | null;
  cancelled_at?: string | null;
  cancelled_by_user_id?: number | null;
  // Kalıcı bildirim outbox'ının işlenme sonucu; cihazın bildirimi gördüğüne
  // dair bir okundu/teslim kanıtı değildir.
  cancellation_notification?: {
    state: 'pending' | 'provider_accepted' | 'suppressed' | 'unavailable';
    processed_at: string | null;
  } | null;
  direction: 'sent' | 'received';
  other_name: string;
  other_photo: string | null;
  // Rezervasyonda recipient hizmeti sunan/fiyat sahibi, requester ödeyendir.
  recipient_price_time: number | string;
  recipient_price: number | string;
  recipient_currency: string;
  pending_reschedule?: MeetupRescheduleRequest | null;
  // Yalnızca POST /meetups yanıtında dolu — sohbette eklenen "sistem mesajı"nın id'si.
  message_id?: number;
};

export type MeetupRescheduleRequest = {
  id: number;
  proposed_at: string;
  source_time_zone?: string | null;
  created_at: string;
  direction: 'sent' | 'received';
  status?: 'pending' | 'accepted' | 'declined' | 'cancelled';
  message_id?: number;
  // Alt kayıt mutasyonu sonrası ana rezervasyonun yeni sürümü.
  record_revision?: number;
};

type PayoutRequest = {
  id: number;
  amount: number;
  currency: string;
  status: string;
  created_at: string;
  processed_at: string | null;
};

export type Call = {
  id: number;
  caller_id: number;
  callee_id: number;
  payer_id?: number;
  payee_id?: number;
  status: 'ringing' | 'accepted' | 'ended' | 'declined' | 'cancelled' | 'missed';
  kind: string;
  price_per_min: string | number;
  price_currency: string;
  payer_currency: string;
  reserved_amount: string | number;
  reserved_seconds: number;
  offer_sdp: string | null;
  answer_sdp: string | null;
  end_reason?: string | null;
  accepted_at: string | null;
  elapsed_seconds?: number;
  viewer_rate_per_min?: number;
  viewer_currency?: string;
  viewer_amount_kind?: 'spent' | 'earned';
  // 9.4.4.5 — yalnız getCall (GET /:id) yoklamasında dolar; diğer yanıtlarda yok.
  renegotiation_offer_sdp?: string | null;
  renegotiation_answer_sdp?: string | null;
  renegotiation_seq?: number;
};

// 9.4.10 — teknik gelen/giden yönünden bağımsız ekonomik rol sunucudan gelir.
// 9.4.10 / D-635 — geçmişte hangi kesit gösteriliyor.
export type CallHistoryFilter = 'all' | 'missed';

export type CallHistoryItem = {
  id: number;
  direction: 'outgoing' | 'incoming';
  other: { id: number; name: string; surname: string | null; photo: string | null };
  status: 'ended' | 'missed' | 'declined' | 'cancelled';
  billed_seconds: number;
  amount: number;
  amount_kind?: 'spent' | 'earned';
  currency: string;
  price_per_min: number;
  created_at: string;
  accepted_at: string | null;
  ended_at: string | null;
};

export type IncomingCall = {
  id: number;
  caller_id: number;
  status: string;
  /** 9.4.14 — aranan tarafın kamerayı açıp açmayacağını bu belirler. */
  kind?: 'audio' | 'video';
  price_per_min: string | number;
  price_currency: string;
  viewer_rate_per_min?: number;
  viewer_currency?: string;
  viewer_amount_kind?: 'spent' | 'earned';
  offer_sdp: string | null;
  caller_name: string | null;
  caller_surname: string | null;
  caller_photo: string | null;
  created_at: string;
};

type WalletData = {
  balance: number;
  pending: number;
  currency: string;
  escrow: number;
  earnings: {
    totalEarned: number;
    inEscrow: number;
    withdrawable: number;
  };
  transactions: {
    amount: number;
    receiver_amount: number | null;
    currency: string;
    type: string;
    status: string;
    created_at: string;
    from_user_id: number | null;
    from_name: string | null;
    to_name: string | null;
  }[];
};

export type Conversation = {
  other_id: number;
  other_name: string;
  other_photo: string | null;
  /** 9.26 / D-641 — resmî hesap (mavi işaret). Eski sunucu göndermez. */
  other_is_official?: boolean;
  last_content: string;
  last_type: string;
  last_at: string;
  last_sender_id: number;
  // Şifreli satırın düz metnini yerel önbellekte bulmak için (9.34 · D-617).
  // Çağrı satırlarında karşılığı yoktur, orada null gelir.
  last_message_id: number | null;
  last_edit_revision?: number;
  unread_count: number;
  muted: boolean;
  // Kullanıcı sohbeti elle "okunmadı" işaretlediyse true (9.26.7.1.2). Okunmamış
  // mesaj sayısından bağımsız: mesajların read_at'i değişmez, bayrak ayrı durur.
  manual_unread: boolean;
};

// Engellenen kullanıcı — GET /messages/blocked
export type CallException = {
  id: number;
  name: string;
  surname: string | null;
  photo_url: string | null;
  // true = her zaman izin ver, false = hiçbir zaman (null bu listede dönmez)
  call_allowed: boolean;
  // Yalnız liste sahibinin bu kişiyi engellemesini gösterir. Ters yöndeki
  // engel mahremdir ve API tarafından açığa çıkarılmaz.
  blocked_by_owner: boolean;
};

export type MessageException = {
  id: number;
  name: string;
  surname: string | null;
  photo_url: string | null;
  // true = her zaman izin ver, false = hiçbir zaman (null bu listede dönmez)
  message_allowed: boolean;
  blocked_by_owner: boolean;
};

export type BlockedUser = {
  id: number;
  name: string;
  photo_url: string | null;
};

// 3.10.5 — bu kişiye bir mesajın ödeyene maliyeti. `amount`/`currency` ödeyenin
// cebinden çıkacak tutar; `seller_*` satıcının ilan ettiği fiyattır.
/** 9.3.3.2 — açık paylaşım. `expires_at` null ise süresiz. */
export type LocationShare = {
  id: number;
  status: 'active' | 'stopped' | 'expired';
  started_at: string;
  expires_at: string | null;
  /** 9.3.3.6 — karşı taraf en son ne zaman baktı; hiç bakılmadıysa null. */
  last_viewed_at?: string | null;
};

/** 9.1.9 — sipariş açıldı. `message_id` sohbete düşen satırın kimliğidir. */
export type VoiceOrderCreated = {
  id: number;
  status: string;
  reserved_seconds: number;
  reserved_amount: number | string;
  payer_currency: string;
  expires_at: string;
  message_id: number;
};

/**
 * 9.1.11 — teslim sonucu. `charged` GERÇEKTEN kesilen tutar, `refunded` ise
 * ısmarlayana geri dönen fark: kısa teslimde ikisi de sıfırdan büyüktür ve
 * arayüz bunu söylemek zorundadır (D-573).
 */
export type VoiceOrderDelivered = {
  order_id: number;
  charged_amount: number;
  refunded: number;
  message: Message;
};

// Signal Protokolü anahtar dizini gövde/yanıt tipleri (9.34 · D-603, D-604).
// Alanlar sunucunun beklediği snake_case ile birebir; anahtarlar base64.
export type KeyBundleUploadBody = {
  registration_id: number;
  identity_key: string;
  signed_prekey: { id: number; key: string; signature: string };
  one_time_prekeys: { id: number; key: string }[];
};

// `device_id` sunucu tarafından ayrılır (9.34 · D-618): aynı kimlik anahtarı
// gelirse o cihazın numarası, yeni bir kimlik gelirse yeni bir numara döner.
export type KeyBundleUploadResult = {
  added: number;
  one_time_prekeys: number;
  device_id: number;
};

// Bir cihaza adreslenmiş şifreli kopya (9.34 · D-623).
export type MessageEnvelope = { user_id: number; device_id: number; content: string };

// Cihazın dizindeki KİMLİĞİ — demet değil. Oturum kurmak için `PrekeyBundle`
// istenmeli; imzalı anahtarın burada olmaması bilerek (9.34 · D-630).
export type DeviceIdentity = {
  device_id: number;
  registration_id: number;
  identity_key: string;
};

export type PrekeyBundle = {
  user_id: number;
  device_id: number;
  registration_id: number;
  identity_key: string;
  signed_prekey: { id: number; key: string; signature: string };
  one_time_prekey: { id: number; key: string } | null;
};

// 9.34 / D-600 — kurtarma koduyla şifrelenmiş geçmiş yedeği. Alanlar sunucu için
// anlamsız baytlardır.
export type EncryptedBackup = { v: number; salt: string; nonce: string; data: string };

export type MessagePriceQuote = {
  amount: number;
  currency: string;
  seller_amount: number;
  seller_currency: string;
  personal: boolean;
  /**
   * 9.1.6 — ses mesajının DAKİKA başına ücreti, ÖDEYENİN para biriminde.
   * Kayıt ekranındaki taksimetre bunu kullanır; profildeki `price_voice`
   * satıcının para biriminde ilan edilmiştir ve cüzdandan çıkan tutar o
   * değildir. Eski sunucu sürümlerinde alan gelmeyebilir.
   */
  voice_per_minute?: number;
  voice_currency?: string;
  voice_seller_per_minute?: number;
  voice_seller_currency?: string;
};

// 3.10.5 — seçilen arama kipinin ödeyene dakika başına maliyeti. Varsayılan uç
// anlık fiyatı, `mode=scheduled` planlı fiyatı verir. `amount` ödeyenin cebinden
// çıkacak ücret; `seller_*` satıcının ilan ettiği ve teklifte dondurulan ücrettir.
export type CallPriceQuote = {
  amount: number;
  currency: string;
  seller_amount: number;
  seller_currency: string;
  personal: boolean;
};

export type Message = {
  caption?: string | null;
  caption_encrypted?: boolean;
  caption_unreadable?: boolean;
  edit_revision?: number;
  edited_at?: string | null;
  can_edit?: boolean;
  /** Keep the wire encryption type after decrypting to renderable text. */
  encrypted_origin?: boolean;
  id: number;
  sender_id: number;
  content: string;
  type: string;
  status: string;
  created_at: string;
  reply_to_id?: number | null;
  // GET /messages tepkileri toplu döndürür; POST /messages hiç döndürmez
  reactions?: { emoji: string; count: number; mine: boolean }[];
  // GET /messages read_at döndürür (gizlilik kapalıysa null); POST /messages hiç döndürmez
  read_at?: string | null;
  photo_location_district?: string | null;
  photo_location_city?: string | null;
  photo_location_source?: 'file_metadata' | 'capture_location' | null;
  photo_location_visible?: boolean;
  /** 9.2.5 — medya mesajının süresi; doğrulanmış değil, gösterim içindir. */
  media_duration_seconds?: number | null;
  /** 9.2 / D-650 — gönderildiği platform; web'den gelen mesaj küçük bir not taşır. */
  sent_via?: 'web' | 'ios' | 'android' | null;
  // 9.32 / D-653 — şifreli mesajda sunucudan GELMEZ: mesaj çözülünce içindeki
  // önizleme buraya konur. D-658: şifresiz mesajda sunucudan gelir (o
  // konuşmada sunucu linki zaten görüyor). İki yolda da karşı taraftan geldiği
  // için güvenilmezdir ve okunurken süzülür (e2ee/messages.ts).
  link_preview?: LinkPreview | null;
  /**
   * 9.1.8 — siparişin CANLI durumu. Kartın sayıları içerikte donuyor, durumu
   * burada; donsaydı teslim edilmiş sipariş hâlâ "bekliyor" görünürdü.
   */
  /** 9.3.3.5 — canlı paylaşımın CANLI durumu; kart bunu okuyor. */
  location_share?: {
    id: number;
    status: 'active' | 'stopped' | 'expired';
    expires_at: string | null;
    last_point_at: string | null;
    last_viewed_at?: string | null;
  } | null;
  voice_order?: {
    status: 'pending' | 'delivered' | 'declined' | 'expired';
    charged?: number | string | null;
    expires_at?: string | null;
  } | null;
};

// Meslek (ESCO) — arama sonucu / profil seçimi. isco_group = 4 haneli ISCO birim kodu.
export type Profession = {
  id: number;
  name: string;
  isco_group: string;
};

// ISCO-08 ana grubu — keşif filtresi kovası.
// Keşfet listesi öğesi — GET /discover
export type DiscoverUser = {
  id: number;
  name: string;
  photo_url: string | null;
  /** 9.26 / D-641 — resmî hesap (mavi işaret). Profil türü de bunu miras alıyor. */
  is_official?: boolean;
  university: string | null;
  profession: string | null;
  price_message: number;
  currency: string;
};

// Eğitim kaydı — serbest liste ({üniversite + ISCED seviyesi}). ISCED (UNESCO):
// 5=Ön lisans, 6=Lisans, 7=Yüksek lisans, 8=Doktora.
export type Education = {
  id: number;
  university_id: number;
  university: string | null;
  isced_level: number;
  account: ProfileAccount | null;
};

// Meslek kaydı — serbest liste (kullanıcı başına birden çok meslek).
export type UserProfession = {
  id: number;
  profession_id: number;
  name: string | null;
  isco_group: string | null;
};

// Dış profilde gösterilen, kullanıcının kendi beyanına dayalı şirket atfı.
// Serbest metin değil, mevcut bir nosubject hesabının kararlı kimliğine bağlıdır.
export type ProfileAccount = {
  id: number;
  username: string;
  name: string;
};

export type ProfileEmployer = ProfileAccount;

// Tam kullanıcı profili — GET /users/:id. DiscoverUser'ın tüm alanlarını içerir.
/** 9.35.3 · D-830 — one photo or video on a profile, with a short-lived read link. */
export type ProfileMediaItem = {
  id: string;
  url: string;
  mime_type: string;
  duration_seconds: number | null;
  /** Own list only: the archive path it came from, to skip it on the next import. */
  source_key?: string | null;
};

export type UserProfile = DiscoverUser & {
  sms_verified_on?: string | null;
  google_linked_on?: string | null;
  username: string | null;
  surname: string | null;
  university_id: number | null;
  profession_id: number | null;
  profession_group: string | null;
  price_voice: number;
  price_call: number;
  price_call_scheduled?: number;
  price_call_video?: number;
  price_time: number;
  bio: string | null;
  show_read_receipts: boolean;
  last_seen: string | null;
  who_can_message: string;
  keep_archived: boolean;
  deletion_scheduled_at?: string | null;
  education: Education[];
  professions: UserProfession[];
  employer: ProfileEmployer | null;
  university_account: ProfileAccount | null;
};
